// components/StrategyBuilder/ValidationPanel.tsx
import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, XCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  ValidationResult,
  ValidationError,
  ValidationWarning,
} from '../../types'; // ✅ FIXED: Import from correct location

interface ValidationPanelProps {
  validationResult: ValidationResult | null;
}

const ValidationPanel: React.FC<ValidationPanelProps> = ({
  validationResult,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  if (!validationResult) return null;

  const errCount = validationResult.errors.length;
  const warnCount = validationResult.warnings.length;

  // Collapsed: a small pill so the panel never permanently obscures the
  // canvas behind it. The validation info is one click away, not lost.
  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => setCollapsed(false)}
        title="Show strategy validation"
        className={`m-2 inline-flex items-center gap-2 rounded-md border bg-white px-3 py-2 shadow-sm text-sm font-medium hover:bg-gray-50 ${
          validationResult.isValid
            ? 'border-green-200 text-green-700'
            : 'border-red-200 text-red-700'
        }`}
      >
        {validationResult.isValid ? (
          <CheckCircle className="w-4 h-4" />
        ) : (
          <XCircle className="w-4 h-4" />
        )}
        <span>Validation</span>
        {errCount > 0 && <span className="text-red-600">{errCount} err</span>}
        {warnCount > 0 && <span className="text-yellow-600">{warnCount} warn</span>}
        <ChevronRight className="w-4 h-4 text-gray-400" />
      </button>
    );
  }

  return (
    <div
      className="bg-white border-l border-gray-200 w-80"
      style={{
        maxHeight: '82vh',         // Or use '600px' for a fixed pixel value
        overflowY: 'auto',         // Always allow vertical scrolling
      }}
    >
      <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-200 p-4">
        <h3 className="text-lg font-medium text-gray-800">
          Strategy Validation
        </h3>
        <div className="flex items-center gap-2">
          <div
            className={`flex items-center ${
              validationResult.isValid ? 'text-green-600' : 'text-red-600'
            }`}
          >
            {validationResult.isValid ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <XCircle className="w-5 h-5" />
            )}
          </div>
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            title="Collapse panel (reveal canvas)"
            className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="p-4">
        {/* Validation Summary */}
        <div className="mb-4 p-3 bg-gray-50 rounded-md">
          <div className="text-sm">
            <span className="font-medium">Status: </span>
            <span
              className={
                validationResult.isValid ? 'text-green-600' : 'text-red-600'
              }
            >
              {validationResult.isValid ? 'Valid' : 'Invalid'}
            </span>
          </div>
          <div className="text-sm mt-1">
            <span className="font-medium">Last checked: </span>
            <span className="text-gray-600">
              {new Date().toLocaleTimeString()}
            </span>
          </div>
          {(validationResult.errors.length > 0 ||
            validationResult.warnings.length > 0) && (
            <div className="text-sm mt-1">
              <span className="font-medium">Issues: </span>
              <span className="text-red-600">
                {validationResult.errors.length} errors
              </span>
              {validationResult.warnings.length > 0 && (
                <>
                  <span className="text-gray-400 mx-1">•</span>
                  <span className="text-yellow-600">
                    {validationResult.warnings.length} warnings
                  </span>
                </>
              )}
            </div>
          )}
        </div>

        {validationResult.errors.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-red-800 mb-3 flex items-center">
              <XCircle className="w-4 h-4 mr-1" />
              Errors ({validationResult.errors.length})
            </h4>
            <div className="space-y-2">
              {validationResult.errors.map((error, index) => (
                <ValidationItem key={index} item={error} type="error" />
              ))}
            </div>
          </div>
        )}

        {validationResult.warnings.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-yellow-800 mb-3 flex items-center">
              <AlertTriangle className="w-4 h-4 mr-1" />
              Warnings ({validationResult.warnings.length})
            </h4>
            <div className="space-y-2">
              {validationResult.warnings.map((warning, index) => (
                <ValidationItem key={index} item={warning} type="warning" />
              ))}
            </div>
          </div>
        )}

        {validationResult.isValid && validationResult.warnings.length === 0 && (
          <div className="flex items-center justify-center py-8 text-green-600">
            <CheckCircle className="w-8 h-8 mr-2" />
            <div className="text-center">
              <p className="text-lg font-medium">Strategy is valid!</p>
              <p className="text-sm text-gray-600 mt-1">
                All TSL requirements are met
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper component for displaying validation items
interface ValidationItemProps {
  item: ValidationError | ValidationWarning;
  type: 'error' | 'warning';
}

const ValidationItem: React.FC<ValidationItemProps> = ({ item, type }) => {
  const isError = type === 'error';
  const bgColor = isError ? 'bg-red-50' : 'bg-yellow-50';
  const borderColor = isError ? 'border-red-200' : 'border-yellow-200';
  const textColor = isError ? 'text-red-800' : 'text-yellow-800';
  const metaColor = isError ? 'text-red-600' : 'text-yellow-600';
  const Icon = isError ? XCircle : AlertTriangle;
  const iconColor = isError ? 'text-red-600' : 'text-yellow-600';

  return (
    <div
      className={`flex items-start p-3 ${bgColor} rounded-md border ${borderColor}`}
    >
      <Icon className={`w-4 h-4 ${iconColor} mt-0.5 mr-2 flex-shrink-0`} />
      <div className="flex-1">
        <p className={`text-sm ${textColor} leading-relaxed`}>{item.message}</p>
        <div className="flex items-center justify-between mt-1">
          {item.blockId && (
            <p className={`text-xs ${metaColor} font-mono`}>
              Block: {item.blockId}
            </p>
          )}
          {'severity' in item && (
            <span
              className={`text-xs ${metaColor} capitalize px-1.5 py-0.5 rounded ${
                item.severity === 'high'
                  ? 'bg-red-100'
                  : item.severity === 'medium'
                  ? 'bg-yellow-100'
                  : 'bg-gray-100'
              }`}
            >
              {item.severity}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ValidationPanel;
